import React, { useState } from "react";

import SendIcon from "@mui/icons-material/Send";
import { Chip, CircularProgress, Divider, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { PsychologyReverse, ThinPlusInCircle, UploadFile } from "assets/material-custom-icons";
import { AmortizationDrawer } from "components/Deal-amortization/AmortizationDrawer";
import { Menu } from "components/popups";
import { Notice } from "components/Prompts/Notice";
import { useActionHook, useIsViewerPermission, useModals, usePrompt } from "hooks";
import {
  IApiServiceError,
  useDealActions,
  useDealSelector,
  useDebtActions,
  useUserBlankTemplateIdSelector
} from "store";
import { DebtSegment } from "store/types";
import pickFiles from "utils/pick-files";

const today = new Date().toISOString().split("T")[0];

export const AIBox: React.FC = () => {
  const deal = useDealSelector();
  const blankTemplateId = useUserBlankTemplateIdSelector();

  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [segment, setSegment] = useState<Partial<DebtSegment> | null>(null);
  const [askAILoading, setAskAILoading] = useState<boolean>(false);

  const { errorModal } = useModals();
  const openPrompt = usePrompt();
  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const queryClient = useQueryClient();
  const { createDealWithSegmentAction, withdrawalsAskAIAction } = useDebtActions();
  const { getDealAndStoreAction } = useDealActions();
  const { action: createDealWithSegment, loading: createDealWithSegmentLoading } =
    useActionHook(createDealWithSegmentAction);

  const handleSubmit = async () => {
    if (!deal) return;
    const trimmedValue = input.trim();
    if (!trimmedValue && !attachment) return;
    try {
      setAskAILoading(true);

      const debtSegment = await withdrawalsAskAIAction({
        dealId: deal.id,
        ...(attachment && { file: attachment }),
        ...(trimmedValue && { text: trimmedValue })
      });
      setSegment(debtSegment);
      setInput("");
      setAttachment(null);
    } catch (error) {
      errorModal({ msg: (error as IApiServiceError).message });
    } finally {
      setAskAILoading(false);
    }
  };

  const handleAddFiles = async () => {
    if (isAnalystViewerPermission) {
      openPrompt({ Component: Notice, props: { type: "trial" } });
      return;
    }
    const files = await pickFiles({ multiple: true });
    if (!files) return;
    setAttachment(files[0]);
  };

  const handleRemoveFile = () => {
    setAttachment(null);
  };

  const handleApply = async (segments: DebtSegment[], loan_name?: string) => {
    if (!loan_name) return;

    const dealData = {
      parent_id: deal.id,
      title: loan_name,
      deal_stage_id: deal.deal_stage_id,
      asset_class_id: deal.asset_class_id,
      sub_asset_class_id: deal.sub_asset_class_id || undefined,
      template_id: blankTemplateId,
      type: "debt"
    };

    try {
      await createDealWithSegment(dealData, segments);
      await getDealAndStoreAction(deal.id);
      queryClient.invalidateQueries({ queryKey: ["amortizationTable", deal.id, today] });
      setSegment(null);
    } catch (error) {
      console.error("Error creating deal with segments:", error);
    }
  };

  return (
    <>
      <Stack
        sx={{
          flexDirection: "row",
          gap: "10px",
          p: "16px",
          borderRadius: "8px",
          boxShadow: "0 1px 2px 0 #1212171a",
          bgcolor: "white"
        }}
      >
        <Stack sx={{ flexDirection: "row", gap: "6px", pt: "6px" }}>
          <PsychologyReverse />
          <Typography
            sx={{ fontSize: "16px", lineHeight: "24px", fontWeight: 500, whiteSpace: "nowrap", color: "#071F35" }}
          >
            AI Agent
          </Typography>
        </Stack>
        <Stack sx={{ flexGrow: 1, gap: "10px", overflow: "hidden" }}>
          <TextField
            fullWidth
            value={input}
            disabled={askAILoading}
            multiline
            maxRows={4}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Add loan details or file and AI will create a deal automatically"
            sx={{
              "& .MuiInputBase-root": {
                // height: "32px",
                fontSize: "14px"
              }
            }}
          />
          <Stack sx={{ flexDirection: "row", gap: "6px", overflow: "hidden", flexWrap: "wrap" }}>
            {attachment && (
              <Chip
                key={attachment.name}
                label={attachment.name}
                size="small"
                disabled={askAILoading}
                onDelete={() => handleRemoveFile()}
              />
            )}
          </Stack>
        </Stack>
        <Stack sx={{ flexDirection: "row", gap: "6px", alignItems: "center", height: "24px", pt: "12px" }}>
          <Menu
            options={[
              {
                title: "Attach File",
                icon: <UploadFile />,
                onClick: handleAddFiles
              }
            ]}
            icon={<ThinPlusInCircle sx={{ width: "24px", height: "24px" }} />}
            iconButtonProps={{ size: "sm", disabled: askAILoading }}
          />
          <Divider orientation="vertical" sx={{ borderColor: "#DBE8F5", height: "24px" }} />
          {askAILoading ? (
            <CircularProgress size={24} />
          ) : (
            <IconButton
              onClick={handleSubmit}
              size="sm"
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.dark"
                }
              }}
            >
              <SendIcon />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <AmortizationDrawer
        open={Boolean(segment)}
        onClose={() => setSegment(null)}
        onApply={handleApply}
        loading={createDealWithSegmentLoading}
        newDeal
        {...(segment && { presetValues: segment })}
      />
    </>
  );
};
